import type { Campaign } from 'c-sdk';

export type SettledTarget = {
    /** Expected impressions on settled days, or null when nothing is settled yet. */
    target: number | null;
    /** Delivery against that target, as the campaign list shows it; null alongside no target. */
    percent: number | null;
};

/**
 * UL-8133 -- the unfiltered headline for an insertion-order targeting campaign (`vistar_us2`).
 *
 * The builder's live run (UL-8132) writes the settled target into `playout_stats.expected_impacts`
 * and the delivery against it into `playout_percentage`. Both are read as they are, never
 * recomputed, so the analytics headline, the DSP PCA report and the campaign list agree. With no
 * `playout_stats` (no live run yet) there is no target to show.
 */
export const settledTarget = (
    campaign: Pick<Campaign, 'playout_stats' | 'playout_percentage'> | null | undefined,
): SettledTarget => {
    const stats = campaign?.playout_stats;
    if (stats == null) return { target: null, percent: null };

    const target = Number(stats.expected_impacts);
    if (!Number.isFinite(target)) return { target: null, percent: null };

    // The API writes 0% when nothing was expected; that is "no target yet", not a 0% delivery.
    const percent =
        target > 0 && campaign?.playout_percentage != null
            ? Number(campaign.playout_percentage)
            : null;

    return { target, percent: percent != null && Number.isFinite(percent) ? percent : null };
};
